import * as React from 'react';
import cx from 'clsx';
import { ImageType } from './AssetLayer';
import styles from './Asset.module.scss';

export const createImageSrcSet = (
  imageNormal?: string,
  imageRetina?: string
) => {
  if (!imageRetina && imageNormal) return imageNormal;
  else if (imageRetina && imageNormal) {
    return `${imageNormal} 1x, ${imageRetina} 2x`;
  } else return '';
};

export const createSourceMedia = (
  src: ImageType,
  min?: number,
  max?: number
) => {
  let media = '';
  if (min) media = `(min-width: ${min}px)`;
  if (min && max) media = `${media} and `;
  if (max) media = `${media}(max-width: ${max}px)`;

  const isAvailableModernFormat = src && src.modern;
  const srcNormal = isAvailableModernFormat
    ? `${src.modern?.normal}`
    : src.normal;
  const srcRetina = isAvailableModernFormat
    ? `${src.modern?.retina}`
    : src.retina;
  const srcSet = createImageSrcSet(srcNormal, srcRetina);
  const type = isAvailableModernFormat ? { type: 'image/webp' } : {};

  return {
    media,
    srcSet,
    ...type,
  };
};

const Asset = ({
  intersected,
  assetRef,
  loadingAnimation,
  type,
  fallbackBackgroundColor,
  alt,
  attributes,
  sizedSources,
  videoSrc,
  imageSrc,
  isAnimationAsset,
  assetName,
  animationStyles,
}: {
  intersected: boolean;
  assetRef?: any;
  loadingAnimation?: boolean;
  fallbackBackgroundColor?: string;
  alt?: string;
  type: 'video' | 'image';
  isAnimationAsset?: boolean;
  attributes?: { [key: string]: boolean | string | { [key: string]: string } };
  videoSrc?: string;
  sizedSources?: {
    min?: number;
    max?: number;
    src: {
      normal: string;
      retina?: string;
      modern?: {
        normal: string;
        retina?: string;
      };
    };
  }[];
  imageSrc?: {
    normal: string;
    retina?: string;
    modern?: {
      normal: string;
      retina?: string;
    };
  };
  assetName?: string;
  animationStyles?: { animationDelay: string };
}) => {
  const backgroundAndAnimationStyles = {
    backgroundColor: fallbackBackgroundColor || '',
    ...animationStyles,
  };

  if (!intersected) {
    return (
      <img
        alt={'asset'}
        ref={assetRef}
        className={cx(
          styles.notVisible,
          loadingAnimation && styles.animation,
          assetName
        )}
        style={backgroundAndAnimationStyles}
        src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      />
    );
  }

  if (!sizedSources && !imageSrc) {
    switch (type) {
      case 'video':
        return (
          <video
            className={cx(styles.video, {
              [styles.animation]: loadingAnimation,
            })}
            style={backgroundAndAnimationStyles}
            data-testid="asset-video-element"
            ref={assetRef}
          />
        );
      case 'image':
        return (
          <img
            className={cx(styles.image, {
              [styles.animation]: loadingAnimation,
              [styles.animationAsset]: isAnimationAsset,
            })}
            style={backgroundAndAnimationStyles}
            {...attributes}
            data-testid="asset-image-element"
            alt={alt}
            ref={assetRef}
          />
        );
    }
  }

  let srcSet = createImageSrcSet(imageSrc?.normal, imageSrc?.retina);
  let sizes = '';

  // support for safari where picture tag is not supported
  if (sizedSources) {
    srcSet = '';
    sizes = '';
    const isRetina = Number(window.devicePixelRatio) > 1;
    sizedSources.forEach(s => {
      const imgSrc = isRetina ? s.src.retina || s.src.normal : s.src.normal;

      srcSet = `${srcSet}, ${imgSrc} ${s.max || s.min}w`;
      sizes = `${sizes} ${
        s.max ? `(max-width: ${s.max}px) ${Math.floor(s.max / 2)}px,` : ``
      } ${s.min ? `(min-width: ${s.min}px) ${s.min}px,` : ``}`;
    });
  }

  const isAvailableModernFormat = imageSrc && imageSrc.modern;

  const returnImagesInModernFormat = () => {
    return sizedSources ? (
      sizedSources.map((src, i) => (
        <source
          key={i}
          data-testid={`asset-image-source-element-${i}`}
          {...createSourceMedia(src.src, src.min, src.max)}
        />
      ))
    ) : (
      <source
        srcSet={
          imageSrc &&
          createImageSrcSet(imageSrc.modern?.normal, imageSrc.modern?.retina)
        }
        type="image/webp"
        data-testid="asset-image-source-element"
      />
    );
  };

  switch (type) {
    case 'video':
      return (
        <video
          className={cx(styles.video, {
            [styles.animation]: loadingAnimation,
          })}
          style={backgroundAndAnimationStyles}
          data-testid="asset-video-element"
          src={videoSrc}
          {...attributes}
          ref={assetRef}
        />
      );
    case 'image':
      return (
        <picture className={styles.picture}>
          {isAvailableModernFormat ? returnImagesInModernFormat() : undefined}
          {sizedSources
            ? sizedSources.map((src, i) => (
                <source
                  key={i}
                  {...createSourceMedia(src.src, src.min, src.max)}
                />
              ))
            : undefined}
          <img
            className={cx(styles.image, assetName, {
              [styles.animation]: loadingAnimation,
              [styles.animationAsset]: isAnimationAsset,
            })}
            style={backgroundAndAnimationStyles}
            {...attributes}
            data-testid="asset-image-element"
            alt={alt}
            srcSet={srcSet}
            src={
              intersected
                ? sizedSources
                  ? sizedSources[0].src.normal
                  : imageSrc?.normal
                : undefined
            }
            sizes={sizes}
            ref={assetRef}
          />
        </picture>
      );
  }
};

export default Asset;
